import type { IStackStyles, IStackTokens } from "@fluentui/react";
import { Slider, Stack } from "@fluentui/react";
import React, { useEffect, useState } from "react";
import type { NodeProps, Node } from "@xyflow/react";
import type { ExtraNodeData, InterfaceToRecord } from "./action-config-node";
import ActionConfigNode from "./action-config-node";
import type { PulseData } from "../../../api";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { useUpdateNodeData } from "@/lib/hooks/reactflow";

type PulseNode = Node<InterfaceToRecord<PulseData & ExtraNodeData>>;

const stackStyles: Partial<IStackStyles> = { root: { width: 300 } };
const stackTokens: IStackTokens = { childrenGap: 20 };
const sliderValueFormat = (value: number): string => `${value}s`;

export default function Pulse(props: NodeProps<PulseNode>): JSX.Element {
    const { t }: { t: TFunction } = useTranslation();

    // Keep delay and duration in ms
    const [delay, setDelay] = useState(props.data.delayMs ?? 0);
    const [duration, setDuration] = useState(props.data.durationMs ?? 1000);
    const updateNode = useUpdateNodeData<PulseNode>(props.id);

    // We have to set the data to prevent undefined values
    useEffect(() => {
        updateNode({ delayMs: delay, durationMs: duration });
    }, [delay, duration, updateNode]);

    // Convert ms to seconds for display, and update state in ms when slider changes
    const handleDelayChange = (newValue: number): void => {
        setDelay(newValue * 1000);
    };

    const handleDurationChange = (newValue: number): void => {
        setDuration(newValue * 1000);
    };

    return (
        <ActionConfigNode nodeKind={props.type} {...props.data}>
            <Stack tokens={stackTokens} styles={stackStyles}>
                <Slider
                    className={"nodrag"}
                    label={t("component.actionConfigNodes.pulse.delay")}
                    max={20}
                    step={0.5}
                    valueFormat={sliderValueFormat}
                    showValue
                    value={delay / 1000}
                    onChange={handleDelayChange}
                />
                <Slider
                    className={"nodrag"}
                    label={t("component.actionConfigNodes.pulse.duration")}
                    max={20}
                    step={0.5}
                    valueFormat={sliderValueFormat}
                    showValue
                    value={duration / 1000}
                    onChange={handleDurationChange}
                />
            </Stack>
        </ActionConfigNode>
    );
}
